import { useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import LangInput from "@/components/LangInput";
import { CheckCircle, Sparkle, PenNib, ArrowRight } from "@phosphor-icons/react";

const LANGS = ["English", "Español", "Français", "Deutsch", "हिन्दी", "தமிழ்", "മലയാളം", "বাংলা", "中文", "Português", "العربية", "Kiswahili"];

export default function ExpertsApply() {
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null); // pending | approved | rejected
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    profession: "",
    institution: "",
    expertise: "",
    biography: "",
    country: "",
    state: "",
    city: "",
    languages: ["English"],
    website: "",
    orcid: "",
    linkedin: "",
    why: "",
    topics: "",
    agree: false,
  });
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    if (!form.agree) return toast.error("Please accept the community guidelines to continue.");
    if (!form.full_name || !form.email || !form.profession || form.biography.length < 20) {
      return toast.error("Fill in name, email, profession and a bio (≥20 chars).");
    }
    setSubmitting(true);
    try {
      const external_links = [];
      if (form.website) external_links.push({ label: "Website", url: form.website });
      if (form.orcid) external_links.push({ label: "ORCID", url: form.orcid });
      if (form.linkedin) external_links.push({ label: "LinkedIn", url: form.linkedin });
      const payload = {
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        profession: form.profession.trim(),
        institution: form.institution.trim() || undefined,
        expertise: form.expertise.split(",").map((s) => s.trim()).filter(Boolean).slice(0, 12),
        biography: form.biography.trim(),
        location: { country: form.country || undefined, state: form.state || undefined, city: form.city || undefined },
        languages: form.languages,
        external_links,
        why_i_want_to_contribute: form.why.trim() || undefined,
        topics_i_want_to_cover: form.topics.split(",").map((s) => s.trim()).filter(Boolean).slice(0, 20),
        agree_guidelines: true,
      };
      const { data } = await http.post("/experts/apply", payload);
      setStatus(data?.status || "pending");
      window.scrollTo({ top: 0, behavior: "smooth" });
      toast.success("Application received — our editors will review it within a few days.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit application");
    } finally {
      setSubmitting(false);
    }
  };

  if (status === "pending") {
    return (
      <main className="bg-[#F5EBDC] min-h-screen pt-32 md:pt-36 pb-32 px-5 md:px-10" data-testid="experts-apply-success">
        <div className="max-w-[720px] mx-auto rounded-2xl bg-white border border-black/10 p-8 md:p-10 shadow-sm text-[#1B4332]">
          <div className="w-14 h-14 rounded-full bg-emerald-100 grid place-items-center mb-3">
            <CheckCircle size={26} weight="fill" className="text-emerald-600" />
          </div>
          <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">
            Application received
          </div>
          <h2 className="mt-1 font-serif-display text-3xl">Thanks — we'll be in touch soon.</h2>
          <p className="mt-2 text-sm text-[#3A3830]">
            Every NaturePulse Expert application is reviewed by our editorial team. If approved, we'll email you at{" "}
            <span className="font-medium">{form.email}</span> and set up your Expert Space.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/experts"
              data-testid="experts-apply-back"
              className="inline-flex items-center gap-2 rounded-full bg-[#1B4332] text-[#F5EBDC] px-5 py-2.5 font-mono-tech text-[10px] tracking-[0.3em] uppercase"
            >
              Explore the directory <ArrowRight size={12} weight="bold" />
            </Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="bg-[#F5EBDC] min-h-screen pt-32 md:pt-36 pb-32 px-5 md:px-10" data-testid="experts-apply">
      <div className="max-w-[820px] mx-auto">
        <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
          / Apply to become a NaturePulse Expert
        </div>
        <h1 className="mt-2 font-serif-display text-4xl md:text-5xl leading-[0.98] tracking-tight">
          One identity. Many communities. A trusted voice for nature.
        </h1>
        <p className="mt-3 font-serif-display text-base md:text-lg italic text-[#3A3830]">
          Scientists, farmers, birdwatchers, teachers, indigenous knowledge holders — every kind of nature expertise belongs here. Tell us who you are.
        </p>

        <div className="mt-8 rounded-2xl bg-white border border-black/10 p-6 md:p-8 shadow-sm text-[#1B4332] space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Full name" testid="apply-name">
              <input value={form.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="apply-name-input" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" />
            </Field>
            <Field label="Email" testid="apply-email">
              <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="apply-email-input" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" />
            </Field>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Profession" hint="e.g. Marine Ecologist, Farmer, Naturalist" testid="apply-profession">
              <input value={form.profession} onChange={(e) => set("profession", e.target.value)} data-testid="apply-profession-input" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" />
            </Field>
            <Field label="Institution / organisation (optional)">
              <input value={form.institution} onChange={(e) => set("institution", e.target.value)} className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" />
            </Field>
          </div>
          <Field label="Areas of expertise" hint="Comma-separated, e.g. Coral Reefs, Marine Ecology, Climate Change">
            <input value={form.expertise} onChange={(e) => set("expertise", e.target.value)} data-testid="apply-expertise-input" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" />
          </Field>
          <Field label="Biography" hint="Tell us who you are, what you work on, and how you know nature.">
            <LangInput as="textarea" rows={6} value={form.biography} onChange={(e) => set("biography", e.target.value)} placeholder="A few paragraphs about your work…" testid="apply-bio" />
          </Field>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Country">
              <input value={form.country} onChange={(e) => set("country", e.target.value)} className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" />
            </Field>
            <Field label="State / Province">
              <input value={form.state} onChange={(e) => set("state", e.target.value)} className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" />
            </Field>
            <Field label="City">
              <input value={form.city} onChange={(e) => set("city", e.target.value)} className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" />
            </Field>
          </div>
          <Field label="Languages you can publish or answer in">
            <div className="flex flex-wrap gap-1.5">
              {LANGS.map((L) => {
                const active = form.languages.includes(L);
                return (
                  <button
                    key={L}
                    type="button"
                    onClick={() => set("languages", active ? form.languages.filter((x) => x !== L) : [...form.languages, L])}
                    data-testid={`apply-lang-${L.toLowerCase()}`}
                    className={`rounded-full px-3 py-1 font-mono-tech text-[10px] tracking-[0.22em] uppercase border ${
                      active
                        ? "border-[color:var(--np-brand,#B85F2A)] bg-[color:var(--np-brand,#B85F2A)] text-white"
                        : "border-black/15 bg-white text-[#241209] hover:bg-black/5"
                    }`}
                  >{L}</button>
                );
              })}
            </div>
          </Field>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Website (optional)">
              <input value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="https://" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" />
            </Field>
            <Field label="ORCID / Google Scholar (optional)">
              <input value={form.orcid} onChange={(e) => set("orcid", e.target.value)} placeholder="https://" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" />
            </Field>
            <Field label="LinkedIn (optional)">
              <input value={form.linkedin} onChange={(e) => set("linkedin", e.target.value)} placeholder="https://" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" />
            </Field>
          </div>
          <Field label="Topics you'd like to cover" hint="Comma-separated. Helps editors route your articles.">
            <input value={form.topics} onChange={(e) => set("topics", e.target.value)} data-testid="apply-topics-input" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" />
          </Field>
          <Field label="Why do you want to contribute?">
            <LangInput as="textarea" rows={4} value={form.why} onChange={(e) => set("why", e.target.value)} placeholder="What would you write about, and who would benefit?" testid="apply-why" />
          </Field>

          <label className="flex items-start gap-2 text-xs text-[#3A3830]">
            <input
              type="checkbox"
              checked={form.agree}
              onChange={(e) => set("agree", e.target.checked)}
              data-testid="apply-agree"
              className="mt-0.5"
            />
            <span>
              I agree to the NaturePulse <Link to="/terms" className="underline">Community Guidelines</Link> and understand that verification confirms identity or credentials — not endorsement of opinions.
            </span>
          </label>

          <div className="pt-2">
            <button
              onClick={submit}
              disabled={submitting || !form.agree}
              data-testid="apply-submit"
              className="inline-flex items-center gap-2 rounded-full bg-[#1B4332] text-[#F5EBDC] px-6 py-2.5 font-mono-tech text-[10px] tracking-[0.3em] uppercase disabled:opacity-50"
            >
              <Sparkle size={12} weight="fill" /> {submitting ? "Submitting…" : "Submit application"}
            </button>
          </div>
        </div>

        <div className="mt-8 rounded-2xl border border-dashed border-black/15 bg-[#FBF2E1]/60 p-5 text-[#3A3830] text-sm flex items-start gap-3" data-testid="experts-apply-info">
          <PenNib size={18} weight="fill" className="text-[color:var(--np-brand,#B85F2A)] mt-0.5" />
          <div>
            <p className="font-medium">What happens next?</p>
            <p className="mt-1">Our editors review every application. Once approved, we set up your Expert Space so your articles, research and answers automatically flow into the topic and geographic pages they're relevant to.</p>
          </div>
        </div>
      </div>
    </main>
  );
}

const Field = ({ label, hint, children, testid }) => (
  <div data-testid={testid}>
    <label className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)]">{label}</label>
    <div className="mt-1">{children}</div>
    {hint && <div className="mt-1 text-[10px] text-[#3A3830]">{hint}</div>}
  </div>
);
